import * as React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Ingredient {
  name: string;
  quantity?: string;
}

interface Macros {
  [key: string]: string;
}

interface RecipeOutput {
  "recipe name": string;
  ingredients: Ingredient[];
  spices?: Ingredient[];
  needed_appliances?: string[];
  macros?: Macros;
  "preparation time"?: string;
  "cooking time"?: string;
  difficulty?: number;
  instructions?: string[];
}

interface Props {
  data: RecipeOutput;
  onBack?: () => void;
}

export const RecipeResult: React.FC<Props> = ({ data, onBack }) => {
  const difficulty = data.difficulty ?? 0;

  const [animate, setAnimate] = React.useState(false);
  React.useEffect(() => {
    // Only animate when a meaningful difficulty (>0) is present
    if ((data.difficulty ?? 0) > 0) {
      const t = setTimeout(() => setAnimate(true), 120);
      return () => clearTimeout(t);
    }
    // if no difficulty or zero, ensure no animate
    setAnimate(false);
    return;
  }, [data.difficulty]);

  const DifficultyBar: React.FC<{ difficulty: number; animate: boolean }> = ({ difficulty, animate }) => {
    const filled = Math.max(0, Math.min(5, Math.round(difficulty)));
    return (
      <div aria-hidden className="flex items-center gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="relative h-2 w-6 rounded bg-border overflow-hidden">
            <div
              className={`absolute left-0 top-0 bottom-0 rounded ${i < filled ? "bg-primary" : "bg-border"}`}
              style={{
                transformOrigin: "left",
                transform: animate && i < filled ? "scaleX(1)" : "scaleX(0)",
                width: "100%",
                transition: "transform 450ms cubic-bezier(.2,.9,.2,1)",
                transitionDelay: `${i * 150}ms`,
              }}
            />
          </div>
        ))}
      </div>
    );
  };

  const normalizeList = (input: any): Ingredient[] => {
    if (!input) return [];
    // If it's an array of strings
    if (Array.isArray(input) && input.every((i) => typeof i === "string")) {
      return input.map((name) => ({ name }));
    }

    // If it's an object keyed map { name: qty }
    if (!Array.isArray(input) && typeof input === "object") {
      return Object.entries(input).map(([k, v]) => ({ name: k, quantity: String(v) }));
    }

    // If it's an array of objects with varying keys, attempt to extract name and quantity
    if (Array.isArray(input)) {
      return input.map((it) => {
        if (!it) return { name: "Unknown" };
        if (typeof it === "string") return { name: it };
        // Common key names to look for
        const name = it.name ?? it.item ?? it.ingredient ?? it.title ?? it["ingredient name"] ?? it["recipe name"];
        const quantity = it.quantity ?? it.qty ?? it.amount ?? it.measurement ?? it["quantity"];
        return { name: name ?? "Unknown", quantity: quantity ? String(quantity) : undefined };
      });
    }

    return [];
  };

  const ingredients = normalizeList((data as any).ingredients);
  // normalize and dedupe spices by name (preserve first occurrence)
  const _rawSpices = normalizeList((data as any).spices ?? (data as any).spice ?? []);
  const spices = (() => {
    const map = new Map<string, Ingredient>();
    for (const s of _rawSpices) {
      const key = (s.name || "").toLowerCase().trim();
      if (!map.has(key)) map.set(key, s);
    }
    return Array.from(map.values());
  })();

  // set of spice names for quick lookup to avoid duplicates in ingredient groups
  const spiceNames = new Set(spices.map((s) => (s.name || "").toLowerCase().trim()));

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-6">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-foreground">{data["recipe name"]}</h2>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            {data["preparation time"] && <span className="rounded-md bg-muted px-2 py-1">Prep: {data["preparation time"]}</span>}
            {data["cooking time"] && <span className="rounded-md bg-muted px-2 py-1">Cook: {data["cooking time"]}</span>}
            {data.macros && <span className="rounded-md bg-muted px-2 py-1">Macros available</span>}
            {data.needed_appliances && (
              <span className="rounded-md bg-muted px-2 py-1">Appliances: {data.needed_appliances.join(", ")}</span>
            )}
          </div>
          {/* Difficulty shown under specifications */}
          <div className="mt-3">
            <h5 className="mb-1 text-xs font-medium text-muted-foreground">Difficulty</h5>
            <div className="flex items-center gap-3">
              {difficulty > 0 ? (
                <>
                  <DifficultyBar difficulty={difficulty} animate={animate} />
                  <div className="text-sm text-muted-foreground">{difficulty}/5</div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground">Difficulty: —</div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 flex flex-col gap-2">
          {onBack && (
            <Button variant="outline" onClick={onBack} size="sm">Back</Button>
          )}
        </div>
      </div>

  <div className="grid gap-4 md:grid-cols-4">
        <Card className="p-4 md:col-span-2">
          <h4 className="mb-2 text-sm font-medium text-muted-foreground">Ingredients</h4>
          {ingredients.length === 0 ? (
            <div className="text-muted-foreground text-sm">No ingredients provided</div>
          ) : (
            <div className="space-y-4 text-sm">
              {/* classify ingredients into groups using keyword heuristics */}
              {(() => {
                const proteinKeys = ["chicken", "beef", "salmon", "tofu", "turkey", "egg", "eggs", "pork", "steak"];
                const vegetableKeys = ["carrot", "onion", "garlic", "pepper", "broccoli", "zucchini", "spinach", "tomato"];
                const sideKeys = ["rice", "pasta", "potato", "couscous", "quinoa", "bread", "noodle"];

                const groups: Record<string, Ingredient[]> = { Proteins: [], Vegetables: [], Sides: [], Other: [] };

                const seen = new Set<string>();
                for (const ing of ingredients) {
                  const name = (ing.name || "").toLowerCase().trim();
                  // skip items that are also listed as spices
                  if (spiceNames.has(name)) continue;
                  // skip duplicates by name
                  if (seen.has(name)) continue;
                  seen.add(name);

                  if (proteinKeys.some((k) => name.includes(k))) {
                    groups.Proteins.push(ing);
                  } else if (vegetableKeys.some((k) => name.includes(k))) {
                    groups.Vegetables.push(ing);
                  } else if (sideKeys.some((k) => name.includes(k))) {
                    groups.Sides.push(ing);
                  } else {
                    groups.Other.push(ing);
                  }
                }

                const renderGroup = (title: string, items: Ingredient[], first = false) => {
                  if (!items || items.length === 0) return null;
                  return (
                    <div key={title} className={`${first ? "" : "border-t border-dashed border-border pt-4"}`}>
                      <h5 className="mb-2 text-xs font-medium text-muted-foreground">{title}</h5>
                      <ul className="space-y-2">
                        {items.map((it, i) => (
                          <li key={i} className="flex justify-between">
                            <span>{it.name}</span>
                            <span className="text-muted-foreground">{it.quantity}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                };

                return (
                  <>
                    {renderGroup("Proteins", groups.Proteins, true)}
                    {renderGroup("Vegetables", groups.Vegetables)}
                    {renderGroup("Sides", groups.Sides)}
                    {renderGroup("Other", groups.Other)}
                  </>
                );
              })()}
            </div>
          )}
        </Card>

  <div className="space-y-6 md:col-span-2 md:space-y-0 md:flex md:flex-col md:gap-6">
          <Card className="p-4">
            <h4 className="mb-2 text-sm font-medium text-muted-foreground">Spices</h4>
            <ul className="space-y-2 text-sm">
              {spices.length > 0 ? (
                spices.map((s, idx) => (
                  <li key={idx} className="flex justify-between">
                    <span>{s.name}</span>
                    <span className="text-muted-foreground">{s.quantity}</span>
                  </li>
                ))
              ) : (
                <li className="text-muted-foreground">No spices provided</li>
              )}
            </ul>

            {data.needed_appliances && (
              <div className="mt-4">
                <h5 className="mb-2 text-xs font-medium text-muted-foreground">Appliances</h5>
                <div className="flex flex-wrap gap-2">
                  {data.needed_appliances.map((a, i) => (
                    <span key={i} className="rounded-full bg-primary/10 px-3 py-1 text-xs">{a}</span>
                  ))}
                </div>
              </div>
            )}
          </Card>

          <Card className="p-4">
            <h4 className="mb-2 text-sm font-medium text-muted-foreground">Nutrition</h4>
            <div className="space-y-2 text-sm">
              {data.macros ? (
                Object.entries(data.macros).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="capitalize">{k.replace(/per portion\s*/i, '')}</span>
                    <span className="text-muted-foreground">{v}</span>
                  </div>
                ))
              ) : (
                <div className="text-muted-foreground">No macros provided</div>
              )}

              {/* Difficulty moved to the header */}
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-6">
        <h4 className="mb-4 text-lg font-medium text-foreground">Instructions</h4>
        <ol className="list-decimal space-y-3 pl-5 text-sm">
          {((data.instructions ?? []) as string[]).map((raw, i) => {
            const sanitizeStep = (s: string) => s.replace(/^\s*\d+(?:[.)]|(?:\.\d+))+\s*/g, '').trim();
            const step = sanitizeStep(String(raw || ''));
            return (
              <li key={i} className="text-foreground">{step}</li>
            );
          })}
        </ol>
      </Card>
    </div>
  );
};
